import { Info } from "@phosphor-icons/react";

// Small inline tooltip (native title, no provider needed). Max ~20 words, plain English.
export const Tip = ({ text }) => (
  <span className="inline-flex align-middle ml-1 text-zinc-300 hover:text-zinc-500 cursor-help"
    title={text} data-testid="tip">
    <Info size={12} weight="bold" />
  </span>
);

const PLATFORM_NAMES = {
  reddit: "Reddit", youtube: "YouTube", autocomplete: "Google",
  google: "Google", amazon: "Amazon", walmart: "Walmart", etsy: "Etsy",
  tiktok: "TikTok", pinterest: "Pinterest",
};
export const platformName = (k) => PLATFORM_NAMES[(k || "").toLowerCase()] || (k || "");

export const evidenceLabel = (url) => {
  const u = (url || "").toLowerCase();
  if (u.includes("reddit.com")) return "Open Reddit Discussion";
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "Open YouTube Discussion";
  if (u.includes("google.com/search") || u.includes("google.com")) return "Open Google Search";
  if (u.includes("trends.google")) return "Open Google Trends";
  if (u.includes("amazon.")) return "Open Amazon Listing";
  if (u.includes("pinterest.")) return "Open Pinterest";
  if (u.includes("tiktok.")) return "Open TikTok";
  return "Open Source";
};

const pctHigh = (dist) => {
  const d = dist || {};
  const t = (d.high || 0) + (d.medium || 0) + (d.low || 0);
  return t ? (d.high || 0) / t : 0;
};

// Deterministic Final Verdict built ONLY from stored pipeline outputs. No new AI.
export const buildVerdict = ({ baseline_score, enriched_score, buying_intent_dist, ai_analysis } = {}) => {
  const a = ai_analysis || {};
  const demand = enriched_score ?? baseline_score ?? 0;
  const viability = a.commercial_viability_score;
  const rec = a.ai_recommendation;

  let label, tone;
  const strong = rec === "Approve" || (viability != null && viability >= 65);
  const weak = rec === "Reject" || (viability != null && viability < 45);
  if (strong && !weak) { label = "Strong Opportunity"; tone = "emerald"; }
  else if (weak) { label = "Weak Opportunity"; tone = "rose"; }
  else { label = "Moderate Opportunity"; tone = "amber"; }

  const bullets = [];
  if (demand >= 75) bullets.push("Demand is strong — many customers actively describe this problem.");
  else if (demand >= 55) bullets.push("Demand is moderate across customer signals.");
  else if (demand) bullets.push("Demand is limited compared with other opportunities.");

  const bi = pctHigh(buying_intent_dist);
  if (bi >= 0.5) bullets.push("Customers show clear intent to spend money to solve it.");
  else if (bi > 0) bullets.push("Purchase intent is mixed — not everyone wants to pay for a fix.");

  if (a.existing_competition || a.market_saturation) {
    const comp = a.existing_competition || a.market_saturation;
    const struct = a.market_structure ? ` and ${String(a.market_structure).toLowerCase()}` : "";
    bullets.push(`Competition is ${String(comp).toLowerCase()}${struct}.`);
  }
  if (a.gross_margin_potential) bullets.push(`Profit margin potential looks around ${a.gross_margin_potential}.`);
  if (a.build_difficulty != null) {
    if (a.build_difficulty <= 4) bullets.push("Manufacturing looks straightforward.");
    else if (a.build_difficulty >= 7) bullets.push("Building and launching this would be complex.");
  }
  if (a.productizable === "No") bullets.push("A physical product may not solve this — customers want repairs or services, not a new product.");

  let nextStep;
  if (rec === "Approve") nextStep = "Recommended next step: mark as a Build Candidate and proceed to Existing Solutions analysis.";
  else if (rec === "Reject") nextStep = "Recommendation: Reject — unlikely to become a successful product business.";
  else nextStep = "Recommended next step: investigate further before committing to build.";

  if (bullets.length === 0) bullets.push("Not enough analysis yet — run demand validation and the AI business assessment first.");
  return { label, tone, bullets, nextStep, hasAnalysis: !!ai_analysis };
};
